// The business's documents (PRD 20): files kept once and put on any number of
// records.
//
// EVERY WRITE RETURNS THE WHOLE DOCUMENT, with every record it is on, so a
// panel replaces its copy rather than re-fetching and briefly showing the old
// one.

import {
  businessApiDelete,
  businessApiGet,
  businessApiPatch,
  businessApiPost,
  businessApiPostForm,
} from '../utils/apiUtils';
import { buildQuery, parseData, parseEnvelope } from './businessApiCommon';
import type { AttachableType } from '../types/documentAttachables';
import {
  DocumentQuery,
  DocumentRole,
  DocumentUploadResult,
  LiveDocument,
} from '../types/businessApi';

const BASE = '/api/v1/business/documents';

export interface DocumentRecord {
  linkable_type: AttachableType;
  linkable_id: string;
}

/** DocumentQuery, with a module's record (a service location) as a filter too. */
export type DocumentListQuery = Omit<DocumentQuery, 'linkable_type'> & { linkable_type?: AttachableType };

export const businessDocumentService = {
  async list(query: DocumentListQuery = {}): Promise<{ documents: LiveDocument[]; nextCursor: string | null }> {
    const { data, meta } = await parseEnvelope<LiveDocument[]>(
      await businessApiGet(`${BASE}${buildQuery({ ...query })}`)
    );
    return { documents: data, nextCursor: (meta.next_cursor as string | null | undefined) ?? null };
  },

  /**
   * Multipart, through the api. With a record, the document lands on it in the
   * same request, and a closed period refuses it before anything is stored.
   */
  async upload(params: {
    file: File;
    record?: DocumentRecord;
    role?: DocumentRole;
    caption?: string;
    /** The small picture drawThumbnail made, when it could (PRD 21 §6.2). */
    thumbnail?: Blob | null;
  }): Promise<DocumentUploadResult> {
    const form = new FormData();
    form.append('file', params.file);
    if (params.thumbnail) form.append('thumbnail', params.thumbnail, 'thumbnail.jpg');
    if (params.record) {
      form.append('linkable_type', params.record.linkable_type);
      form.append('linkable_id', params.record.linkable_id);
    }
    if (params.role) form.append('role', params.role);
    if (params.caption) form.append('caption', params.caption);
    const { data, meta } = await parseEnvelope<LiveDocument>(await businessApiPostForm(BASE, form));
    const duplicate = meta.duplicate_of as DocumentUploadResult['duplicateOf'] | undefined;
    return { document: data, duplicateOf: duplicate ?? null };
  },

  /** Puts a document on another record. Linking one already there is a no-op. */
  async link(
    documentId: string,
    params: DocumentRecord & { role?: DocumentRole; caption?: string | null }
  ): Promise<LiveDocument> {
    return parseData<LiveDocument>(await businessApiPost(`${BASE}/${documentId}/links`, params));
  },

  async updateLink(
    documentId: string,
    linkId: string,
    params: { role?: DocumentRole; caption?: string; position?: number }
  ): Promise<LiveDocument> {
    return parseData<LiveDocument>(
      await businessApiPatch(`${BASE}/${documentId}/links/${linkId}`, params)
    );
  },

  /** Takes a document off one record. It stays in the library. */
  async unlink(documentId: string, linkId: string): Promise<LiveDocument> {
    return parseData<LiveDocument>(await businessApiDelete(`${BASE}/${documentId}/links/${linkId}`));
  },

  /** Soft: off every record at once, restorable until `restorable_until`. */
  async remove(documentId: string): Promise<LiveDocument> {
    return parseData<LiveDocument>(await businessApiDelete(`${BASE}/${documentId}`));
  },

  async restore(documentId: string): Promise<LiveDocument> {
    return parseData<LiveDocument>(await businessApiPost(`${BASE}/${documentId}/restore`));
  },

  /**
   * The bytes. `inline` only changes the server's Content-Disposition, and only
   * for PDFs and web images; everything else always downloads.
   */
  async download(documentId: string, options: { inline?: boolean } = {}): Promise<Blob> {
    const query = options.inline ? '?disposition=inline' : '';
    const response = await businessApiGet(`${BASE}/${documentId}/download${query}`);
    if (!response.ok) {
      await parseData(response); // throws BusinessApiError with the server's message
      throw new Error('Could not download the document');
    }
    return response.blob();
  },

  /** The small picture of it, when `has_thumbnail` says there is one. */
  async thumbnail(documentId: string): Promise<Blob> {
    const response = await businessApiGet(`${BASE}/${documentId}/thumbnail`);
    if (!response.ok) {
      await parseData(response);
      throw new Error('Could not load the thumbnail');
    }
    return response.blob();
  },
};

export default businessDocumentService;
